import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Avatar,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { format, parseISO } from 'date-fns';
import Scrollbar from '../scrollbar';

const TABLE_HEAD = [
  { id: 'employee', label: 'Employee', alignRight: false },
  { id: 'date', label: 'Date', alignRight: false },
  { id: 'checkIn', label: 'Check In', alignRight: false },
  { id: 'checkOut', label: 'Check Out', alignRight: false },
  { id: 'totalHours', label: 'Total Hours', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

const AttendanceTable = ({
  attendance,
  isLoading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onViewAttendance,
  onEditAttendance,
  onDeleteAttendance,
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Present':
        return 'success';
      case 'Absent':
        return 'error';
      case 'On Leave':
        return 'warning';
      case 'Remote Work':
        return 'info';
      case 'Late':
        return 'warning';
      case 'Half Day':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Card>
      <Scrollbar>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map((headCell) => (
                  <TableCell key={headCell.id} align={headCell.alignRight ? 'right' : 'left'}>
                    {headCell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : attendance?.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                    <Paper sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2">No attendance records found.</Typography>
                    </Paper>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {attendance.map((record) => (
                  <TableRow hover key={record.id}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {record.employee?.full_name?.charAt(0)?.toUpperCase() || 'N'}
                        </Avatar>
                        <Stack spacing={0}>
                          <Typography variant="subtitle2">{record.employee?.full_name || 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {record.employee?.employee_id || 'N/A'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDate(record.date)}</TableCell>
                    <TableCell>{formatTime(record.check_in_time)}</TableCell>
                    <TableCell>{formatTime(record.check_out_time)}</TableCell>
                    <TableCell>
                      {record.total_hours ? `${record.total_hours} hrs` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.status}
                        color={getStatusColor(record.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Open Employee Dashboard">
                          <span>
                            <IconButton
                              size="small"
                              component={RouterLink}
                              to={`/hr/employee-dashboard?employee_id=${record.employee?.id}`}
                              sx={{ color: 'info.main' }}
                              disabled={!record.employee?.id}
                            >
                              <DashboardIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => onViewAttendance(record)}
                          sx={{ color: 'primary.main' }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onEditAttendance(record)}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteAttendance(record)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Card>
  );
};

AttendanceTable.propTypes = {
  attendance: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      employee: PropTypes.shape({
        id: PropTypes.number,
        full_name: PropTypes.string,
        employee_id: PropTypes.string,
      }),
      date: PropTypes.string.isRequired,
      check_in_time: PropTypes.string,
      check_out_time: PropTypes.string,
      total_hours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string.isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onViewAttendance: PropTypes.func.isRequired,
  onEditAttendance: PropTypes.func.isRequired,
  onDeleteAttendance: PropTypes.func.isRequired,
};

export default AttendanceTable;